import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Download, Trophy, Target, Flame, Printer, Layers } from "lucide-react";
import { jsPDF } from "jspdf";

const API = '/api';

export default function MatchResults() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedMatch, setSelectedMatch] = useState("");
  const [results, setResults] = useState(null);
  const [allMatchResults, setAllMatchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchMatches();
    } else {
      setMatches([]);
      setSelectedMatch("");
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events?t=${Date.now()}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API}/matches?event_id=${selectedEvent}&t=${Date.now()}`);
      setMatches(res.data);
    } catch (error) {
      toast.error("Failed to fetch matches");
    }
  };

  const generateReport = async () => {
    if (!selectedMatch) {
      toast.error("Please select a match");
      return;
    }
    
    setGenerating(true);
    setResults(null);
    setAllMatchResults([]);
    
    try {
      if (selectedMatch === "all") {
        // Fetch results for all matches
        const allResults = [];
        for (const match of matches) {
          if (match.is_championship) {
            // For championship matches, use the championship-scores endpoint
            const res = await axios.get(`${API}/matches/${match.id}/championship-scores`);
            allResults.push({
              match,
              leaderboard: res.data.leaderboard || [],
              shots_per_round: 'N/A',
              is_championship: true,
              source_matches: res.data.source_matches || []
            });
          } else {
            const res = await axios.get(`${API}/leaderboard/${match.id}`);
            allResults.push({
              match,
              leaderboard: res.data.leaderboard || [],
              shots_per_round: res.data.shots_per_round || match.shots_per_round || 25
            });
          }
        }
        setAllMatchResults(allResults);
      } else {
        // Check if selected match is a championship
        const selectedMatchData = matches.find(m => m.id === selectedMatch);
        if (selectedMatchData?.is_championship) {
          const res = await axios.get(`${API}/matches/${selectedMatch}/championship-scores`);
          setResults({
            ...res.data,
            is_championship: true,
            match_name: selectedMatchData.name
          });
        } else {
          // Fetch single match results
          const res = await axios.get(`${API}/leaderboard/${selectedMatch}`);
          setResults(res.data);
        }
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = () => {
    if (!results && allMatchResults.length === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const eventName = events.find(e => e.id === selectedEvent)?.name || "Unknown Event";
    
    const addMatchToPDF = (matchData, matchInfo, isFirst = false) => {
      let y = isFirst ? 20 : 30;
      
      if (!isFirst) {
        doc.addPage();
      }

      // Title
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("NMLRA Trap Tracker", pageWidth / 2, y, { align: "center" });
      y += 10;

      doc.setFontSize(16);
      doc.text("Match Results Report", pageWidth / 2, y, { align: "center" });
      y += 10;

      // Match info
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Event: ${eventName}`, pageWidth / 2, y, { align: "center" });
      y += 6;
      doc.text(`Match: ${matchInfo.name}`, pageWidth / 2, y, { align: "center" });
      y += 6;
      doc.text(`Shots per Round: ${matchData.shots_per_round || 25}`, pageWidth / 2, y, { align: "center" });
      y += 15;

      // Table header
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Rank", 14, y);
      doc.text("Shooter ID", 35, y);
      doc.text("Shooter Name", 65, y);
      doc.text("Best Score", 130, y);
      doc.text("Longest Run", 160, y);
      y += 3;
      doc.line(14, y, pageWidth - 14, y);
      y += 6;

      // Table rows
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      
      const leaderboard = matchData.leaderboard || [];
      leaderboard.forEach((entry) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        
        doc.text(`${entry.rank}`, 14, y);
        doc.text(`${entry.shooter_id_number || '-'}`, 35, y);
        doc.text(`${entry.shooter_name}`, 65, y);
        doc.text(`${entry.best_score || 0}/${matchData.shots_per_round || 25}`, 130, y);
        doc.text(`${entry.best_streak || 0}`, 160, y);
        y += 6;
      });

      // Footer
      y += 10;
      doc.setFontSize(10);
      doc.line(14, y, pageWidth - 14, y);
      y += 8;
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, y, { align: "center" });
    };

    if (allMatchResults.length > 0) {
      // All matches PDF
      allMatchResults.forEach((item, index) => {
        addMatchToPDF(item, item.match, index === 0);
      });
      doc.save(`all_match_results_${eventName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    } else if (results) {
      // Single match PDF
      const matchInfo = matches.find(m => m.id === selectedMatch) || { name: "Unknown Match" };
      addMatchToPDF(results, matchInfo, true);
      doc.save(`match_results_${matchInfo.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    }
    
    toast.success("PDF downloaded!");
  };

  const printReport = () => {
    window.print();
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Match results are only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="match-results-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Match Results
          </h1>
          <p className="text-slate-500 mt-1">View top scores for each shooter by match</p>
        </div>
      </div>

      {/* Report Generator */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Trophy className="text-[#1B3B6F]" />
            Generate Match Results Report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Event</label>
              <Select value={selectedEvent || "none"} onValueChange={(val) => setSelectedEvent(val === "none" ? "" : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select an event...</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Select Match</label>
              <Select 
                value={selectedMatch || "none"} 
                onValueChange={(val) => setSelectedMatch(val === "none" ? "" : val)}
                disabled={!selectedEvent}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedEvent ? "Choose a match" : "Select event first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a match...</SelectItem>
                  {matches.length > 0 && (
                    <SelectItem value="all" className="font-bold text-amber-600">
                      <span className="flex items-center gap-2">
                        <Layers size={14} /> All Matches ({matches.length})
                      </span>
                    </SelectItem>
                  )}
                  {matches.map((match) => (
                    <SelectItem key={match.id} value={match.id}>
                      {match.name} ({match.shots_per_round || 25} shots)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateReport}
                disabled={generating || !selectedMatch}
                className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90 w-full"
                data-testid="generate-results-btn"
              >
                {generating ? "Generating..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Display - Single Match */}
      {results && (
        <div className="space-y-6 print:space-y-4" id="results-content">
          {/* Action Buttons */}
          <div className="flex gap-2 print:hidden">
            <Button onClick={downloadPDF} className="bg-green-700 hover:bg-green-600">
              <Download size={16} className="mr-2" />
              Download PDF
            </Button>
            <Button onClick={printReport} variant="outline">
              <Printer size={16} className="mr-2" />
              Print
            </Button>
          </div>

          {/* Match Info Card */}
          <Card className="hero-gradient text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Trophy size={40} className="text-amber-300" />
                <div>
                  <h2 className="font-serif text-2xl font-bold">
                    {matches.find(m => m.id === selectedMatch)?.name}
                  </h2>
                  <p className="text-white/80 font-mono text-sm">
                    {results.shots_per_round || 25} shots per round • {results.leaderboard?.length || 0} shooters
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Target className="text-[#1B3B6F]" />
                Top Scores by Shooter
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {results.leaderboard?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="w-16">Rank</th>
                        <th>Shooter ID</th>
                        <th>Shooter Name</th>
                        <th className="text-center">Best Score</th>
                        <th className="text-center">
                          <span className="flex items-center justify-center gap-1">
                            <Flame size={14} className="text-orange-500" />
                            Longest Run
                          </span>
                        </th>
                        <th className="text-center">Total Rounds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.leaderboard.map((entry) => (
                        <tr 
                          key={entry.shooter_id || entry.id} 
                          className={
                            entry.rank === 1 ? "bg-yellow-50" : 
                            entry.rank === 2 ? "bg-slate-100" : 
                            entry.rank === 3 ? "bg-amber-50" : ""
                          }
                        >
                          <td className="font-mono text-lg font-bold text-center">
                            {entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : entry.rank}
                          </td>
                          <td className="font-mono text-sm text-slate-500">
                            {entry.shooter_id_number || "-"}
                          </td>
                          <td className="font-medium text-slate-800">
                            {entry.shooter_name}
                          </td>
                          <td className="text-center">
                            <span className="font-mono text-xl font-bold text-green-700">
                              {entry.best_score || 0}
                            </span>
                            <span className="text-slate-400">/{results.shots_per_round || 25}</span>
                          </td>
                          <td className="text-center">
                            <span className="font-mono text-lg font-medium text-orange-600">
                              {entry.best_streak || 0}
                            </span>
                          </td>
                          <td className="text-center font-mono text-slate-500">
                            {entry.total_rounds || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">
                  No scores recorded for this match yet.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          {results.leaderboard?.length > 0 && (
            <div className="flex flex-wrap gap-4 justify-center text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-yellow-50 border border-yellow-200"></span>
                Gold (1st)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-slate-100 border border-slate-200"></span>
                Silver (2nd)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded bg-amber-50 border border-amber-200"></span>
                Bronze (3rd)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Results Display - All Matches */}
      {allMatchResults.length > 0 && (
        <div className="space-y-6 print:space-y-4" id="all-results-content">
          {/* Action Buttons */}
          <div className="flex gap-2 print:hidden">
            <Button onClick={downloadPDF} className="bg-green-700 hover:bg-green-600">
              <Download size={16} className="mr-2" />
              Download All PDFs
            </Button>
            <Button onClick={printReport} variant="outline">
              <Printer size={16} className="mr-2" />
              Print All
            </Button>
          </div>

          {/* Event Header */}
          <Card className="hero-gradient text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Layers size={40} className="text-amber-300" />
                <div>
                  <h2 className="font-serif text-2xl font-bold">
                    All Matches Results
                  </h2>
                  <p className="text-white/80 font-mono text-sm">
                    {events.find(e => e.id === selectedEvent)?.name} • {allMatchResults.length} matches
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Each Match Results */}
          {allMatchResults.map(({ match, leaderboard, shots_per_round }) => (
            <Card key={match.id} className="print:break-inside-avoid">
              <CardHeader className="pb-2 bg-gradient-to-r from-slate-50 to-white">
                <CardTitle className="font-serif text-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="text-amber-500" size={20} />
                    {match.name}
                  </div>
                  <span className="text-sm font-normal text-slate-500">
                    {shots_per_round} shots • {leaderboard.length} shooters
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {leaderboard.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="w-16">Rank</th>
                          <th>Shooter ID</th>
                          <th>Shooter Name</th>
                          <th className="text-center">Best Score</th>
                          <th className="text-center">
                            <span className="flex items-center justify-center gap-1">
                              <Flame size={14} className="text-orange-500" />
                              Longest Run
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaderboard.map((entry) => (
                          <tr 
                            key={entry.shooter_id || entry.id} 
                            className={
                              entry.rank === 1 ? "bg-yellow-50" : 
                              entry.rank === 2 ? "bg-slate-100" : 
                              entry.rank === 3 ? "bg-amber-50" : ""
                            }
                          >
                            <td className="font-mono text-lg font-bold text-center">
                              {entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : entry.rank}
                            </td>
                            <td className="font-mono text-sm text-slate-500">
                              {entry.shooter_id_number || "-"}
                            </td>
                            <td className="font-medium text-slate-800">
                              {entry.shooter_name}
                            </td>
                            <td className="text-center">
                              <span className="font-mono text-xl font-bold text-green-700">
                                {entry.best_score || 0}
                              </span>
                              <span className="text-slate-400">/{shots_per_round}</span>
                            </td>
                            <td className="text-center">
                              <span className="font-mono text-lg font-medium text-orange-600">
                                {entry.best_streak || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-400">
                    No scores recorded yet
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Legend */}
          <div className="flex flex-wrap gap-4 justify-center text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-yellow-50 border border-yellow-200"></span>
              Gold (1st)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-slate-100 border border-slate-200"></span>
              Silver (2nd)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-amber-50 border border-amber-200"></span>
              Bronze (3rd)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
